import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, 
  BookOpen, 
  GraduationCap, 
  Shield, 
  Search,
  CheckCircle,
  XCircle,
  Plus,
  Edit,
  Trash2,
  Mail,
  AlertCircle,
  User,
  Camera,
  Settings,
  ChevronRight,
  Filter,
  MessageSquare,
  Bell,
  Trash,
  Ban,
  RefreshCcw,
  Check,
  Menu,
  X,
  ArrowLeft,
  Send,
  CheckCheck,
  UserX,
  Paperclip,
  FileText,
  Download
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { schoolAdminAPI, authAPI, publicAPI, chatAPI } from '../services/api';
import Button from '../components/common/Button';
import TeacherPortal from './TeacherPortal';

const SchoolAdminPortal = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isNavMenuOpen, setIsNavMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([]);
  const [school, setSchool] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [pendingTeachers, setPendingTeachers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [feedback, setFeedback] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);

  // Form States
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deptForm, setDeptForm] = useState({ name: '', code: '', description: '', contactEmail: '' });
  const [courseForm, setCourseForm] = useState({ title: '', courseCode: '', department: '', description: '', credits: 3, semester: 1, level: 1 });
  const [teacherForm, setTeacherForm] = useState({ fullName: '', email: '', employeeId: '', department: '' });
  const [profileForm, setProfileForm] = useState({ fullName: '', email: '', phone: '', bio: ''});
  const [profileImage, setProfileImage] = useState(() => {
    const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
    return savedUser.profileImage?.url || savedUser.profileImage || null;
  });
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef(null);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '' });
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [selectedTeacherForAccess, setSelectedTeacherForAccess] = useState(null);
  const [selectedDeptsForTeacher, setSelectedDeptsForTeacher] = useState([]);
  const [selectedPrimaryDept, setSelectedPrimaryDept] = useState('');

  // Chat States
  const [contacts, setContacts] = useState([]);
  const [chatSearch, setChatSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatAttachment, setChatAttachment] = useState(null);
  const chatFileInputRef = useRef(null);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);

  const [currentUser, setCurrentUser] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));

  useEffect(() => {
    if (!currentUser || currentUser.role !== 'school_admin') {
      navigate('/');
      return;
    }
    loadDashboard();
    setProfileForm({
      fullName: currentUser.fullName || '',
      email: currentUser.email || '',
      phone: currentUser.phone || '',
      bio: currentUser.bio || ''
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    authAPI.getCurrentUser().then(response => {
      const user = response.data;
      if (!cancelled && user?.role === 'school_admin') {
        setCurrentUser(user);
        setProfileImage(user.profileImage?.url || user.profileImage || null);
        localStorage.setItem('user', JSON.stringify(user));
      }
    }).catch(err => console.error('Failed to refresh school admin permissions:', err));
    return () => { cancelled = true; };
  }, []);

  // Chat Handlers & Effects
  const loadContacts = async (silent = false) => {
    try {
      if (!silent) setLoadingChat(true);
      const res = await chatAPI.getContacts();
      if (res.success) {
        setContacts(res.data || []);
      }
    } catch (err) {
      console.error('Failed to load chat contacts', err);
    } finally {
      if (!silent) setLoadingChat(false);
    }
  };

  const loadMessages = async (contactId, silent = false) => {
    try {
      if (!silent) setLoadingMessages(true);
      const res = await chatAPI.getMessages(contactId);
      if (res.success) {
        setMessages(res.data || []);
      }
    } catch (err) {
      console.error('Failed to load chat messages', err);
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  };

  const handleSelectContact = async (contact) => {
    setSelectedContact(contact);
    setNewMessage('');
    await loadMessages(contact._id);
    await chatAPI.markAsRead(contact._id);
    loadContacts(true);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !chatAttachment) return;
    if (!selectedContact) return;
    try {
      setSendingMsg(true);
      let res;
      if (chatAttachment) {
        const formData = new FormData();
        formData.append('receiverId', selectedContact._id);
        if (newMessage.trim()) formData.append('content', newMessage.trim());
        formData.append('attachment', chatAttachment);
        res = await chatAPI.sendMessage(formData);
      } else {
        res = await chatAPI.sendMessage({
          receiverId: selectedContact._id,
          content: newMessage.trim()
        });
      }
      if (res.success) {
        setMessages(prev => [...prev, res.data]);
        setNewMessage('');
        setChatAttachment(null);
        loadContacts(true);
      }
    } catch (err) {
      console.error('Failed to send message', err);
    } finally {
      setSendingMsg(false);
    }
  };



  useEffect(() => {
    if (activeTab === 'chat') {
      loadContacts();
    }
  }, [activeTab]);

  useEffect(() => {
    // Initial global load for badges
    loadContacts(true);
    
    // Background polling for new messages and badges
    const contactsInterval = setInterval(() => {
      loadContacts(true);
    }, 6000);
    return () => clearInterval(contactsInterval);
  }, []);

  useEffect(() => {
    let msgInterval;
    if (activeTab === 'chat' && selectedContact) {
      msgInterval = setInterval(() => {
        loadMessages(selectedContact._id, true);
      }, 3000);
    }
    return () => {
      if (msgInterval) clearInterval(msgInterval);
    };
  }, [activeTab, selectedContact]);

  const totalUnreadChat = contacts.reduce((sum, c) => sum + (c.unreadCount || 0), 0);

  const location = useLocation();

  // Tab Sync with URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [location.search]);

  useEffect(() => {
    setError('');
    setSuccess('');
    if (activeTab === 'teachers') {
      loadTeachers();
      loadDepartments();
    }
    else if (activeTab === 'approvals') { loadPendingTeachers(); loadDepartments(); }
    else if (activeTab === 'departments') loadDepartments();
    else if (activeTab === 'courses') {
      loadCourses();
      loadDepartments();
    }
    else if (activeTab === 'feedback') loadFeedback();
  }, [activeTab, searchQuery]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const [statsRes, feedbackRes, pendingRes] = await Promise.all([
        schoolAdminAPI.getDashboardStats(),
        schoolAdminAPI.getFeedback(),
        schoolAdminAPI.getPendingTeachers()
      ]);

      if (statsRes.success) {
        const s = statsRes.data.stats;
        setStats([
          { label: "Departments", value: s.totalDepartments.value, icon: GraduationCap, tab: 'departments' },
          { label: "Active Teachers", value: s.totalTeachers.value, icon: Users, tab: 'teachers' },
          { label: "Pending Approvals", value: s.pendingApprovals.value, icon: CheckCircle, tab: 'approvals', color: 'text-orange-600' },
          { label: "Total Courses", value: s.totalCourses.value, icon: BookOpen, tab: 'courses' }
        ]);
        setSchool(statsRes.data.school);
      }

      if (feedbackRes.success) {
        setFeedback(feedbackRes.data);
      }

      if (pendingRes.success) {
        setPendingTeachers(pendingRes.data);
      }
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadTeachers = async () => {
    try {
      setLoading(true);
      const response = await schoolAdminAPI.getTeachers({ search: searchQuery });
      if (response.success) setTeachers(response.data);
    } catch (err) {
      setError('Failed to load teachers');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingTeachers = async () => {
    try {
      setLoading(true);
      const response = await schoolAdminAPI.getPendingTeachers({ search: searchQuery });
      if (response.success) setPendingTeachers(response.data);
    } catch (err) {
      setError('Failed to load pending requests');
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const response = await schoolAdminAPI.getDepartments(); // no search filter — always load all school depts
      if (response.success) setDepartments(response.data);
    } catch (err) {
      setError('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      setLoading(true);
      const response = await schoolAdminAPI.getCourses({ search: searchQuery });
      if (response.success) setCourses(response.data);
    } catch (err) {
      setError('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const loadFeedback = async () => {
    try {
      setLoadingFeedback(true);
      const response = await schoolAdminAPI.getFeedback({ search: searchQuery });
      if (response.success) setFeedback(response.data);
    } catch (err) {
      setError('Failed to load feedback');
    } finally {
      setLoadingFeedback(false);
    }
  };

  // Inline approval state: maps teacherId -> selected dept IDs (null = panel closed)
  const [approvalPanels, setApprovalPanels] = useState({});
  const [approvalPrimaryDepts, setApprovalPrimaryDepts] = useState({});

  const openApprovalPanel = (teacherId, defaultDeptId) => {
    setApprovalPanels(prev => ({ ...prev, [teacherId]: prev[teacherId] !== undefined ? undefined : [] }));
    if (defaultDeptId) {
      setApprovalPrimaryDepts(prev => ({ ...prev, [teacherId]: defaultDeptId }));
    }
  };

  const toggleApprovalDept = (teacherId, deptId) => {
    setApprovalPanels(prev => {
      const current = prev[teacherId] || [];
      const next = current.includes(deptId) ? current.filter(id => id !== deptId) : [...current, deptId];
      return { ...prev, [teacherId]: next };
    });
  };

  // Actions
  const handleApproveTeacher = async (id, permittedDepts, primaryDept) => {
    try {
      const resp = await schoolAdminAPI.approveTeacher(id, { 
        permittedDepartments: permittedDepts || [],
        primaryDepartment: primaryDept || ''
      });
      if (resp.success) {
        setSuccess('Teacher approved!');
        setApprovalPanels(prev => { const n = {...prev}; delete n[id]; return n; });
        setApprovalPrimaryDepts(prev => { const n = {...prev}; delete n[id]; return n; });
        loadPendingTeachers();
        loadDashboard();
      }
    } catch (err) {
      setError(err.message || 'Approval failed');
    }
  };

  const handleRejectTeacher = async (id) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      const resp = await schoolAdminAPI.rejectTeacher(id, { reason });
      if (resp.success) {
        setSuccess('Teacher rejected');
        loadPendingTeachers();
        loadDashboard();
      }
    } catch (err) {
      setError(err.message || 'Rejection failed');
    }
  };

  const handleDeptSubmit = async (e) => {
    e.preventDefault();
    try {
      let resp;
      if (editingItem) {
        resp = await schoolAdminAPI.updateDepartment(editingItem._id, deptForm);
      } else {
        resp = await schoolAdminAPI.createDepartment(deptForm);
      }
      if (resp.success) {
        setSuccess(editingItem ? 'Department updated' : 'Department created');
        setShowForm(false);
        setEditingItem(null);
        loadDepartments();
      }
    } catch (err) {
      setError(err.message || 'Operation failed');
    }
  };

  const handleCourseSubmit = async (e) => {
    e.preventDefault();
    try {
      let resp;
      if (editingItem) {
        resp = await schoolAdminAPI.updateCourse(editingItem._id, courseForm);
      } else {
        resp = await schoolAdminAPI.createCourse(courseForm);
      }
      if (resp.success) {
        setSuccess(editingItem ? 'Course updated' : 'Course created');
        setShowForm(false);
        setEditingItem(null);
        loadCourses();
      }
    } catch (err) {
      setError(err.message || 'Operation failed');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const resp = await authAPI.updateProfile(profileForm);
      if (resp.success) {
        setSuccess('Profile updated successfully');
        localStorage.setItem('user', JSON.stringify(resp.data));
      }
    } catch (err) {
      setError(err.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };
  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Choose an image file for your profile photo.');
      event.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('avatar', file);
    try {
      setAvatarUploading(true);
      const response = await authAPI.updateAvatar(formData);
      const updatedUser = { ...currentUser, profileImage: response.data.profileImage };
      setCurrentUser(updatedUser);
      setProfileImage(response.data.profileImage?.url || null);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setSuccess('Profile photo updated.');
    } catch (err) {
      setError(err.message || 'Failed to upload profile photo');
    } finally {
      setAvatarUploading(false);
      event.target.value = '';
    }
  };

  const handleAddYear = async (deptId) => {
    const year = prompt('Enter year to add (e.g. 1, 2, 3):');
    if (!year || isNaN(year)) return;
    try {
      setLoading(true);
      const resp = await schoolAdminAPI.addDepartmentYear(deptId, year);
      if (resp.success) {
        setSuccess(`Year ${year} added successfully`);
        loadDepartments();
      }
    } catch (err) {
      setError(err.message || 'Failed to add year');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveYear = async (deptId, year) => {
    if (!window.confirm(`Remove Year ${year} from this department?`)) return;
    try {
      setLoading(true);
      const resp = await schoolAdminAPI.removeDepartmentYear(deptId, year);
      if (resp.success) {
        setSuccess(`Year ${year} removed successfully`);
        loadDepartments();
      }
    } catch (err) {
      setError(err.message || 'Failed to remove year');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCourse = async (id) => {
    if(!window.confirm('Are you sure you want to deactivate this course?')) return;
    try {
       await schoolAdminAPI.deleteCourse(id);
       setSuccess('Course deactivated');
       loadCourses();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteDept = async (id) => {
    if(!window.confirm('Are you sure you want to deactivate this department? This will hide it from the library.')) return;
    try {
       await schoolAdminAPI.deleteDepartment(id);
       setSuccess('Department deactivated');
       loadDepartments();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTeacherSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!editingItem) return;
      const resp = await schoolAdminAPI.updateTeacher(editingItem._id, teacherForm);
      if (resp.success) {
        setSuccess('Teacher profile updated');
        setShowForm(false);
        setEditingItem(null);
        loadTeachers();
      }
    } catch (err) {
      setError(err.message || 'Update failed');
    }
  };

  const handleDeleteTeacher = async (id) => {
    if(!window.confirm('Are you sure you want to deactivate this teacher? They will no longer be able to access the portal.')) return;
    try {
       await schoolAdminAPI.deleteTeacher(id);
       setSuccess('Teacher account deactivated');
       loadTeachers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleRestriction = async (id, currentStatus, teacherName) => {
    const newStatus = !currentStatus;
    const action = newStatus ? 'restrict' : 'unrestrict';
    
    if (!window.confirm(`Are you sure you want to ${action} upload access for ${teacherName}?`)) return;
    
    try {
      const resp = await schoolAdminAPI.toggleUserRestriction(id, newStatus);
      if (resp.success) {
        setTeachers(prev => prev.map(t => t._id === id ? { ...t, isRestricted: newStatus } : t));
        setSuccess(`Teacher ${action}ed successfully!`);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError(err.message || `Failed to ${action} teacher`);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const resp = await authAPI.changePassword(passwordForm);
      if (resp.success) {
        setSuccess('Password changed successfully');
        setPasswordForm({ currentPassword: '', newPassword: '' });
      }
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFeedbackStatus = async (id, status) => {
    try {
      await schoolAdminAPI.updateFeedbackStatus(id, status);
      setFeedback(prev => prev.map(f => f._id === id ? { ...f, status } : f));
      setSuccess('Feedback status updated');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Failed to update status');
    }
  };

  const handleDeleteFeedback = async (id) => {
    if (!window.confirm('Delete this feedback?')) return;
    try {
      await schoolAdminAPI.deleteFeedback(id);
      setFeedback(prev => prev.filter(f => f._id !== id));
      setSuccess('Feedback deleted');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Failed to delete feedback');
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Teacher Access Modal */}
      {isAccessModalOpen && selectedTeacherForAccess && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-md" onClick={() => setIsAccessModalOpen(false)} />
          <div className="relative bg-white w-full max-w-2xl rounded-[2.5rem] shadow-2xl overflow-hidden animate-scale-up">
            <div className="p-8 bg-gradient-to-r from-indigo-600 to-blue-700 text-white">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-black uppercase tracking-widest">Manage Privileges</h3>
                  <p className="text-indigo-100 text-xs font-bold mt-1 uppercase tracking-widest">{selectedTeacherForAccess.fullName}</p>
                </div>
                <button onClick={() => setIsAccessModalOpen(false)} className="p-2 hover:bg-white/10 rounded-xl transition-all"><XCircle className="w-6 h-6"/></button>
              </div>
            </div>
            
                <div className="p-8">
                  <div className="mb-8">
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                      <User className="w-4 h-4"/> Primary Department
                    </h4>
                    <select 
                      className="w-full p-4 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 mb-8"
                      value={selectedPrimaryDept}
                      onChange={(e) => setSelectedPrimaryDept(e.target.value)}
                    >
                      <option value="">Select Primary Department</option>
                      {departments.map(d => (
                        <option key={d._id} value={d._id}>{d.name}</option>
                      ))}
                    </select>

                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                      <GraduationCap className="w-4 h-4"/> Department Restrictions
                    </h4>
                <div className={`p-4 rounded-2xl mb-6 flex items-center gap-4 border transition-all ${selectedDeptsForTeacher.length === 0 ? 'bg-red-50 border-red-100' : 'bg-blue-50 border-blue-100'}`}>
                   <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${selectedDeptsForTeacher.length === 0 ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' : 'bg-blue-500 text-white shadow-lg shadow-blue-500/20'}`}>
                      {selectedDeptsForTeacher.length === 0 ? <XCircle className="w-6 h-6"/> : <CheckCircle className="w-6 h-6"/>}
                   </div>
                   <div>
                      <p className="text-[10px] font-black uppercase tracking-widest leading-none mb-1">Current Access Status</p>
                      <p className={`text-xs font-bold ${selectedDeptsForTeacher.length === 0 ? 'text-red-700' : 'text-blue-700'}`}>
                         {selectedDeptsForTeacher.length === 0 
                           ? "NO UPLOAD PRIVILEGES: Teacher cannot access or upload to any department. Please check at least one department below to grant access." 
                           : `RESTRICTED WHITELIST: Teacher is permitted to access strictly the ${selectedDeptsForTeacher.length} selected department(s).`}
                      </p>
                   </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                  {departments.map(dept => {
                    const isSelected = selectedDeptsForTeacher.includes(dept._id);
                    return (
                      <button
                        key={dept._id}
                        onClick={() => {
                          if (isSelected) setSelectedDeptsForTeacher(prev => prev.filter(id => id !== dept._id));
                          else setSelectedDeptsForTeacher(prev => [...prev, dept._id]);
                        }}
                        className={`flex items-center gap-3 p-4 rounded-2xl border transition-all text-left ${
                          isSelected 
                            ? 'bg-indigo-50 border-indigo-200 text-indigo-700 ring-2 ring-indigo-500/20' 
                            : 'bg-white border-slate-100 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-md flex items-center justify-center border transition-all ${isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-300'}`}>
                          {isSelected && <CheckCircle className="w-3 h-3" />}
                        </div>
                        <div>
                          <div className="text-[10px] font-black uppercase tracking-tight leading-tight">{dept.name}</div>
                          <div className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1">{dept.code}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <div className="space-y-6 pt-8 border-t border-slate-100">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                  <Shield className="w-4 h-4"/> Security Actions
                </h4>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={async () => {
                      const newPass = window.prompt('Enter new temporary password for ' + selectedTeacherForAccess.fullName + ':');
                      if (newPass) {
                        try {
                          await schoolAdminAPI.resetUserPassword(selectedTeacherForAccess._id, newPass);
                          setSuccess('Security key updated successfully');
                        } catch (err) {
                          setError('Failed to reset security key');
                        }
                      }
                    }}
                    className="flex-1 flex items-center justify-center gap-3 py-4 bg-slate-50 text-slate-600 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-50 hover:text-amber-700 transition-all border border-slate-100 hover:border-amber-100 group"
                  >
                    <RefreshCcw className="w-4 h-4 group-hover:rotate-180 transition-transform duration-700"/> Reset Password
                  </button>
                  
                  <Button 
                    variant="primary" 
                    className="flex-[2] py-5 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-xl shadow-indigo-600/20"
                    onClick={async () => {
                      try {
                        // Update both primary department and accessibility list
                        await schoolAdminAPI.updateTeacherAccess(selectedTeacherForAccess._id, {
                          permittedDepartments: selectedDeptsForTeacher,
                          primaryDepartment: selectedPrimaryDept
                        });
                        setSuccess('Administrative privileges updated');
                        setIsAccessModalOpen(false);
                        loadTeachers();
                      } catch (err) {
                        setError('System failure during privilege update');
                      }
                    }}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-indigo-900 p-8 text-white shadow-lg lg:px-24">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 max-w-7xl mx-auto w-full">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm border border-white/10">
              <Shield className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">School Admin Portal</h1>
              <p className="text-blue-100 flex items-center gap-2">
                <span className="font-semibold">{school?.name || 'Loading school...'}</span>
                {school?.code && <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">{school.code}</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
             <div className="hidden md:block text-right mr-2">
                <div className="text-sm font-medium">{currentUser.fullName}</div>
                <div className="text-xs text-blue-200">System Admin</div>
             </div>
             <Button variant="secondary" onClick={handleLogout} className="bg-white/10 hover:bg-white/20 border-white/20 text-white">Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 -mt-8">
        {/* Mobile Navigation Dropdown Trigger */}
        <div className="lg:hidden bg-white rounded-2xl shadow-xl mb-8 border border-gray-100 p-2">
          <div className="flex justify-between items-center">
            {(() => {
              const currentTab = [
                { id: 'dashboard', label: 'Overview', icon: Shield },
                { id: 'approvals', label: 'Approvals', icon: CheckCircle },
                { id: 'teachers', label: 'Teachers', icon: Users },
                { id: 'departments', label: 'Departments', icon: GraduationCap },
                { id: 'courses', label: 'Courses', icon: BookOpen },
                { id: 'chat', label: 'Colleague Chat', icon: MessageSquare },
                ...(currentUser.permissions?.canUsePersonalFile !== false ? [{ id: 'personal-files', label: 'Personal Files', icon: FileText }] : []),
                { id: 'feedback', label: 'Feedback', icon: MessageSquare },
                { id: 'profile', label: 'My Profile', icon: User }
              ].find(t => t.id === activeTab);
              
              const Icon = currentTab?.icon || Shield;
              return (
                <div className="flex items-center gap-3 px-4 py-2">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block">Active Section</span>
                    <h3 className="font-bold text-gray-800 text-sm flex items-center gap-2">
                      {currentTab?.label}
                      {activeTab === 'feedback' && feedback.filter(f => f.status === 'new').length > 0 && (
                        <span className="bg-red-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full">
                          {feedback.filter(f => f.status === 'new').length}
                        </span>
                      )}
                      {activeTab === 'approvals' && pendingTeachers.length > 0 && (
                        <span className="bg-orange-100 text-orange-600 text-[9px] font-black px-1.5 py-0.5 rounded-full">
                          {pendingTeachers.length}
                        </span>
                      )}
                    </h3>
                  </div>
                </div>
              );
            })()}
            <button
              onClick={() => setIsNavMenuOpen(true)}
              className="p-3 mr-1 rounded-xl bg-gray-50 text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Desktop Navigation Tabs */}
        <div className="hidden lg:block overflow-x-auto overscroll-x-contain bg-white rounded-2xl shadow-xl mb-8 border border-gray-100 p-1">
          <nav className="flex space-x-1">
            {[
              { id: 'dashboard', label: 'Overview', icon: Shield },
              { id: 'approvals', label: 'Approvals', icon: CheckCircle },
              { id: 'teachers', label: 'Teachers', icon: Users },
              { id: 'departments', label: 'Departments', icon: GraduationCap },
              { id: 'courses', label: 'Courses', icon: BookOpen },
              { id: 'chat', label: 'Colleague Chat', icon: MessageSquare },
              ...(currentUser.permissions?.canUsePersonalFile !== false ? [{ id: 'personal-files', label: 'Personal Files', icon: FileText }] : []),
              { id: 'feedback', label: 'Feedback', icon: MessageSquare },
              { id: 'profile', label: 'My Profile', icon: User }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setShowForm(false); }}
                className={`flex shrink-0 items-center gap-2 px-6 py-3.5 rounded-xl font-medium transition-all duration-200 ${
                  activeTab === tab.id 
                    ? 'text-blue-600 bg-blue-50 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.id === 'feedback' && feedback.filter(f => f.status === 'new').length > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] flex items-center justify-center">
                    {feedback.filter(f => f.status === 'new').length}
                  </span>
                )}
                {tab.id === 'chat' && totalUnreadChat > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] flex items-center justify-center animate-pulse">
                    {totalUnreadChat}
                  </span>
                )}
                {tab.id === 'approvals' && pendingTeachers.length > 0 && (
                  <span className="bg-orange-100 text-orange-600 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    {pendingTeachers.length}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Notifications */}
        <div className="space-y-4 mb-8">
          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 p-4 rounded-2xl flex items-center gap-3 animate-shake">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="flex-1 text-sm font-medium">{error}</p>
              <button onClick={() => setError('')}><XCircle className="w-4 h-4" /></button>
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-100 text-green-700 p-4 rounded-2xl flex items-center gap-3 animate-fade-in">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <p className="flex-1 text-sm font-medium">{success}</p>
              <button onClick={() => setSuccess('')}><XCircle className="w-4 h-4" /></button>
            </div>
          )}
        </div>

        {loading && activeTab !== 'dashboard' ? (
          <div className="flex justify-center items-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="animate-fade-in">
            {activeTab === 'dashboard' && (
              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {stats.map((stat, i) => {
                    const Icon = stat.icon;
                    return (
                      <button 
                        key={i} 
                        onClick={() => setActiveTab(stat.tab)}
                        className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 hover:shadow-xl hover:scale-[1.02] transition-all group text-left"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className={`p-4 rounded-2xl bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors ${stat.color || ''}`}>
                            <Icon className="w-7 h-7" />
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-blue-500 transition-colors" />
                        </div>
                        <div>
                          <div className="text-3xl font-extrabold text-gray-900 mb-1">{stat.value}</div>
                          <div className="text-gray-500 text-sm font-semibold uppercase tracking-wider">{stat.label}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                
                {/* Quick Info / Recent Activity placeholder */}
                {/* Quick Action Lists */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Recent Feedback */}
                  <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
                    <div className="p-6 border-b border-gray-50 flex justify-between items-center bg-gray-50/30">
                      <h3 className="font-bold text-gray-800 flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-blue-500" />
                        Recent Feedback
                      </h3>
                      <button onClick={() => setActiveTab('feedback')} className="text-xs font-bold text-blue-600 hover:text-blue-700 uppercase tracking-wider">View All</button>
                    </div>
                    <div className="p-2 flex-1">
                      {feedback.filter(f => f.status === 'new').length === 0 ? (
                        <div className="py-12 text-center">
                          <CheckCircle className="w-10 h-10 text-green-200 mx-auto mb-2" />
                          <p className="text-gray-400 text-sm font-medium">All caught up!</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {feedback.filter(f => f.status === 'new').slice(0, 3).map(msg => (
                            <button 
                              key={msg._id} 
                              onClick={() => setActiveTab('feedback')}
                              className="w-full text-left p-4 hover:bg-blue-50/50 rounded-2xl transition-all group"
                            >
                              <div className="flex justify-between items-start mb-1">
                                <span className="font-bold text-gray-900 line-clamp-1">{msg.subject}</span>
                                <span className="text-[10px] text-gray-400 whitespace-nowrap ml-2">{new Date(msg.createdAt).toLocaleDateString()}</span>
                              </div>
                              <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{msg.message}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pending Approvals */}
                  <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
                    <div className="p-6 border-b border-gray-50 flex justify-between items-center bg-gray-50/30">
                      <h3 className="font-bold text-gray-800 flex items-center gap-2">
                        <Users className="w-5 h-5 text-orange-500" />
                        Staff Approvals
                      </h3>
                      <button onClick={() => setActiveTab('approvals')} className="text-xs font-bold text-blue-600 hover:text-blue-700 uppercase tracking-wider">View All</button>
                    </div>
                    <div className="p-2 flex-1">
                      {pendingTeachers.length === 0 ? (
                        <div className="py-12 text-center">
                          <Users className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                          <p className="text-gray-400 text-sm font-medium">No pending requests</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {pendingTeachers.slice(0, 3).map(req => (
                            <div key={req._id} className="p-4 bg-gray-50/50 rounded-2xl flex items-center justify-between group hover:bg-white hover:shadow-sm transition-all border border-transparent hover:border-gray-100">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center font-bold text-sm">
                                  {req.fullName[0]}
                                </div>
                                <div>
                                  <div className="font-bold text-gray-900 text-sm">{req.fullName}</div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-[10px] text-gray-500 font-mono">{req.employeeId || 'No ID'}</span>
                                    <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[9px] font-bold">
                                      {req.teacherProfile?.department?.name || 'N/A'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => setActiveTab('approvals')} className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg" title="Review & Approve"><CheckCircle className="w-4 h-4"/></button>
                                <button onClick={() => handleRejectTeacher(req._id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"><XCircle className="w-4 h-4"/></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8">
                   <h2 className="text-xl font-bold mb-6 text-gray-800">School Identity</h2>
                   <div className="grid md:grid-cols-2 gap-8 text-sm">
                      <div className="space-y-4">
                         <div className="flex justify-between p-4 bg-gray-50 rounded-2xl">
                            <span className="text-gray-500 font-medium">Official Name</span>
                            <span className="text-gray-900 font-bold">{school?.name}</span>
                         </div>
                         <div className="flex justify-between p-4 bg-gray-50 rounded-2xl">
                            <span className="text-gray-500 font-medium">University Code</span>
                            <span className="text-gray-900 font-bold tracking-widest">{school?.code}</span>
                         </div>
                      </div>
                      <div className="p-6 bg-blue-50 rounded-3xl border border-blue-100">
                         <h3 className="text-blue-800 font-bold mb-2">Management Notice</h3>
                         <p className="text-blue-700 leading-relaxed italic">
                           "Welcome to your management dashboard. The lists above show urgent items requiring your attention."
                         </p>
                      </div>
                   </div>
                </div>
              </div>
            )}

            {activeTab === 'approvals' && (
              <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
                   <h3 className="font-bold text-gray-800">Teacher Registration Requests</h3>
                   <div className="text-xs font-bold text-orange-600 bg-orange-50 px-3 py-1.5 rounded-full border border-orange-100 uppercase tracking-wider">
                      Action Required
                   </div>
                </div>
                {pendingTeachers.length === 0 ? (
                   <div className="p-20 text-center">
                      <CheckCircle className="w-16 h-16 text-green-200 mx-auto mb-4" />
                      <h3 className="text-lg font-bold text-gray-800">No Pending Requests</h3>
                      <p className="text-gray-500">All teacher registrations have been processed.</p>
                   </div>
                ) : (
                  <table className="w-full text-left">
                    <thead className="bg-gray-50/50 text-xs font-bold uppercase tracking-wider text-gray-500">
                      <tr>
                        <th className="px-8 py-5">Teacher Candidate</th>
                        <th className="px-8 py-5">Employee ID</th>
                        <th className="px-8 py-5 text-right">Decision</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingTeachers.map(req => {
                        const panelDepts = approvalPanels[req._id]; // undefined = closed, [] = open (all access), [...] = some selected
                        const isOpen = panelDepts !== undefined;
                        return (
                          <React.Fragment key={req._id}>
                            <tr className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                              <td className="px-8 py-5">
                                <div className="font-bold text-gray-900">{req.fullName}</div>
                                <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Mail className="w-3 h-3" /> {req.email}</div>
                                <div className="text-[10px] text-blue-600 font-bold uppercase tracking-wider mt-1.5 bg-blue-50 px-2.5 py-0.5 rounded-full inline-block border border-blue-100">Requested Dept: {req.teacherProfile?.department?.name || 'N/A'}</div>
                              </td>
                              <td className="px-8 py-5 font-mono text-sm text-blue-600 font-medium">{req.employeeId || 'N/A'}</td>
                              <td className="px-8 py-5 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => openApprovalPanel(req._id, req.teacherProfile?.department?._id)}
                                    className={`py-2 px-4 rounded-xl text-xs font-bold transition-all border ${isOpen ? 'bg-green-600 text-white border-green-600' : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'}`}
                                  >
                                    {isOpen ? '▲ Set Dept & Approve' : '✓ Approve'}
                                  </button>
                                  <button onClick={() => handleRejectTeacher(req._id)} className="py-2 px-4 rounded-xl text-xs font-bold text-red-500 border border-red-100 hover:bg-red-50 transition-all">
                                    Reject
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {isOpen && (
                              <tr className="bg-green-50/60 border-t border-green-100">
                                <td colSpan={3} className="px-8 py-5">
                                  <div className="flex flex-col gap-6">
                                    {/* Primary Department Assignment Dropdown */}
                                    <div className="bg-white p-6 rounded-2xl border border-green-100 shadow-sm max-w-2xl">
                                      <p className="text-[10px] font-black uppercase tracking-widest text-green-700 mb-3 flex items-center gap-1.5">
                                        <GraduationCap className="w-4 h-4" /> Assign Primary Department
                                      </p>
                                      <select
                                        value={approvalPrimaryDepts[req._id] || ''}
                                        onChange={(e) => setApprovalPrimaryDepts(prev => ({ ...prev, [req._id]: e.target.value }))}
                                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-green-500"
                                      >
                                        <option value="">Select Primary Department</option>
                                        {departments.map(dept => (
                                          <option key={dept._id} value={dept._id}>{dept.name}</option>
                                        ))}
                                      </select>
                                      <p className="text-[9px] text-gray-400 font-semibold mt-2 uppercase tracking-wide">
                                        Note: This sets the teacher's main academic department affiliation.
                                      </p>
                                    </div>

                                    <div className="flex flex-wrap items-start gap-4">
                                      <div className="flex-1">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-green-700 mb-3 flex items-center gap-1.5">
                                          <GraduationCap className="w-3.5 h-3.5" /> Department Permissions (Access Privileges)
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                          {/* Individual dept toggles */}
                                          {departments.map(dept => {
                                            const sel = panelDepts.includes(dept._id);
                                            return (
                                              <button
                                                key={dept._id}
                                                onClick={() => toggleApprovalDept(req._id, dept._id)}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-wide border transition-all ${sel ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:bg-indigo-50'}`}
                                              >
                                                {sel && <CheckCircle className="w-3 h-3" />} {dept.name}
                                              </button>
                                            );
                                          })}
                                        </div>
                                        <p className={`text-[10px] font-bold mt-2 ${panelDepts.length === 0 ? 'text-red-500' : 'text-indigo-600'}`}>
                                          {panelDepts.length === 0 ? '⚠ No departments selected — teacher will have ZERO upload privileges' : `✓ Restricted access granted for ${panelDepts.length} department(s)`}
                                        </p>
                                      </div>
                                      <div className="flex flex-col gap-2 pt-1 shrink-0">
                                        <button
                                          onClick={() => handleApproveTeacher(req._id, panelDepts, approvalPrimaryDepts[req._id])}
                                          className="py-2.5 px-6 rounded-xl text-xs font-black uppercase tracking-widest bg-green-600 text-white hover:bg-green-700 shadow transition-all"
                                        >
                                          Confirm Approval
                                        </button>
                                        <button
                                          onClick={() => setApprovalPanels(prev => { const n={...prev}; delete n[req._id]; return n; })}
                                          className="py-2 px-6 rounded-xl text-xs font-bold text-gray-500 border border-gray-200 hover:bg-gray-100 transition-all"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeTab === 'teachers' && (
              <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                  <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search active staff..."
                      className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                </div>

                <table className="w-full text-left">
                  <thead className="bg-gray-50/50 text-xs font-bold uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="px-8 py-5">Active Teacher</th>
                      <th className="px-8 py-5">Department</th>
                      <th className="px-8 py-5">Contact</th>
                      <th className="px-8 py-5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teachers.map(teacher => (
                      <tr key={teacher._id} className="border-t border-gray-50 hover:bg-gray-50/80 transition-colors">
                        <td className="px-8 py-6">
                           <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold">
                                 {teacher.fullName.split(' ').map(n=>n[0]).join('')}
                              </div>
                              <span className="font-bold text-gray-900">{teacher.fullName}</span>
                           </div>
                        </td>
                        <td className="px-8 py-6">
                          <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold border border-blue-100">
                            {teacher.teacherProfile?.department?.name || 'General Staff'}
                          </span>
                        </td>
                        <td className="px-8 py-6 text-sm font-medium text-gray-600">{teacher.email}</td>
                        <td className="px-8 py-6 text-right">
                           <div className="flex justify-end gap-2">
                              <button 
                                onClick={() => {
                                  setSelectedTeacherForAccess(teacher);
                                  // Normalize: permittedDepartments may be populated objects {_id,name} or plain strings
                                  const existingPerms = (teacher.teacherProfile?.permittedDepartments || []).map(d => d._id ? d._id.toString() : d.toString());
                                  setSelectedDeptsForTeacher(existingPerms);
                                  setSelectedPrimaryDept(teacher.teacherProfile?.department?._id || teacher.teacherProfile?.department || '');
                                  setIsAccessModalOpen(true);
                                }}
                                className="p-2.5 bg-indigo-50 text-indigo-700 rounded-xl border border-indigo-100 shadow-sm hover:bg-indigo-100 transition-colors"
                                title="Manage Privileges"
                              >
                                <Shield className="w-4 h-4"/>
                              </button>
                              <button 
                                onClick={() => handleToggleRestriction(teacher._id, teacher.isRestricted, teacher.fullName)}
                                className={`p-2.5 rounded-xl transition-colors border shadow-sm hover:shadow-md flex items-center gap-1 ${
                                  teacher.isRestricted 
                                    ? 'text-amber-600 bg-amber-50 border-amber-100' 
                                    : 'text-gray-500 bg-white border-gray-100 hover:bg-gray-50'
                                }`}
                                title={teacher.isRestricted ? "Unrestrict" : "Restrict"}
                              >
                                <Ban className="w-4 h-4"/>
                              </button>
                              <button onClick={() => handleDeleteTeacher(teacher._id)} className="p-2.5 text-red-500 hover:bg-red-50 rounded-xl transition-colors border border-transparent hover:border-red-100 shadow-sm hover:shadow-md"><Trash2 className="w-4 h-4"/></button>
                           </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'departments' && (
               <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                   <h3 className="font-bold text-gray-800">Academic Departments</h3>
                   <Button icon={Plus} onClick={() => { setEditingItem(null); setDeptForm({name:'', code:'', description:'', contactEmail:''}); setShowForm(true); }} className="px-6 rounded-2xl">New Department</Button>
                </div>

                {showForm && activeTab === 'departments' && (
                  <div className="p-8 bg-blue-50/50 border-b border-blue-100 animate-slide-down">
                    <form onSubmit={handleDeptSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
                       <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1 underline decoration-blue-200">Department Name</label>
                            <input className="w-full p-3 border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" value={deptForm.name} onChange={e=>setDeptForm({...deptForm, name: e.target.value})} required placeholder="e.g. Computer Science"/>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Official Code</label>
                            <input className="w-full p-3 border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-mono tracking-widest" value={deptForm.code} onChange={e=>setDeptForm({...deptForm, code: e.target.value.toUpperCase()})} required placeholder="e.g. CS01"/>
                          </div>
                       </div>
                       <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Contact Email</label>
                            <input className="w-full p-3 border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" value={deptForm.contactEmail} onChange={e=>setDeptForm({...deptForm, contactEmail: e.target.value})} placeholder="dept@ddu.edu.et"/>
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Description</label>
                            <textarea className="w-full p-3 border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none h-24" value={deptForm.description} onChange={e=>setDeptForm({...deptForm, description: e.target.value})}></textarea>
                          </div>
                       </div>
                       <div className="md:col-span-2 flex justify-end gap-3 pt-4">
                          <Button variant="outline" type="button" onClick={()=>setShowForm(false)}>Cancel</Button>
                          <Button type="submit">{editingItem ? 'Update Department' : 'Save Department'}</Button>
                       </div>
                    </form>
                  </div>
                )}

                <table className="w-full text-left">
                  <thead className="bg-gray-50/50 text-xs font-bold uppercase tracking-wider text-gray-500">
                    <tr>
                      <th className="px-8 py-5">Name & Code</th>
                      <th className="px-8 py-5">Head of Department</th>
                      <th className="px-8 py-5">Academic Years</th>
                      <th className="px-8 py-5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departments.map(dept => (
                      <tr key={dept._id} className="border-t border-gray-50 hover:bg-gray-50/80 transition-colors">
                        <td className="px-8 py-6">
                           <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center font-bold">
                                 {dept.code}
                              </div>
                              <span className="font-bold text-gray-900">{dept.name}</span>
                           </div>
                        </td>
                        <td className="px-8 py-6">
                           <div className="flex items-center gap-2 group">
                              <User className="w-4 h-4 text-gray-300" />
                              <span className="text-gray-600 font-medium group-hover:text-blue-600 transition-colors">
                                 {dept.headOfDepartment?.fullName || 'Not Assigned'}
                              </span>
                           </div>
                        </td>
                        <td className="px-8 py-6">
                           <div className="flex flex-wrap gap-2">
                             {dept.activeYears?.sort().map(year => (
                               <span key={year} className="bg-blue-50 text-blue-600 px-2 py-1 rounded-lg text-xs font-bold border border-blue-100 flex items-center gap-1">
                                 Year {year}
                                 <button onClick={() => handleRemoveYear(dept._id, year)} className="hover:text-red-500 transition-colors"><XCircle className="w-3 h-3"/></button>
                               </span>
                             ))}
                             <button onClick={() => handleAddYear(dept._id)} className="bg-gray-50 text-gray-400 hover:text-blue-500 hover:bg-white border border-dashed border-gray-200 px-2 py-1 rounded-lg text-xs font-bold transition-all"><Plus className="w-3 h-3"/></button>
                           </div>
                        </td>
                        <td className="px-8 py-6 text-right">
                           <div className="flex justify-end gap-2">
                               <button onClick={() => { setEditingItem(dept); setDeptForm({name: dept.name, code: dept.code, description: dept.description, contactEmail: dept.contactEmail}); setShowForm(true); }} className="p-2.5 text-blue-500 hover:bg-blue-50 rounded-xl transition-colors border border-transparent hover:border-blue-100 shadow-sm hover:shadow-md"><Edit className="w-4 h-4"/></button>
                               <button onClick={() => handleDeleteDept(dept._id)} className="p-2.5 text-red-500 hover:bg-red-50 rounded-xl transition-colors border border-transparent hover:border-red-100 shadow-sm hover:shadow-md"><Trash2 className="w-4 h-4"/></button>
                           </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'courses' && (
               <div className="space-y-6">
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-gray-50/50">
                    <div className="relative flex-1 max-w-md w-full">
                       <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                       <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search academic catalog..."
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-2xl text-sm shadow-sm focus:ring-2 focus:ring-blue-500 outline-none"
                       />
                    </div>
                    <Button icon={Plus} onClick={() => { setEditingItem(null); setCourseForm({title: '', courseCode: '', department: '', description: '', credits: 3, semester: 1, level: 1}); setShowForm(true); }} className="rounded-2xl">Create Course</Button>
                  </div>

                  {showForm && activeTab === 'courses' && (
                    <div className="p-8 bg-blue-50/30 border-b border-blue-50">
                       <form onSubmit={handleCourseSubmit} className="max-w-5xl">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                             <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Course Title</label>
                                <input className="w-full p-3 border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-semibold text-gray-800" value={courseForm.title} onChange={e=>setCourseForm({...courseForm, title: e.target.value})} required/>
                             </div>
                             <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Code</label>
                                <input className="w-full p-3 border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-mono font-semibold text-gray-800" value={courseForm.courseCode} onChange={e=>setCourseForm({...courseForm, courseCode: e.target.value})} required/>
                             </div>
                             <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Department Scope</label>
                                <select 
                                  className="w-full p-3 border border-blue-100 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 outline-none font-semibold text-gray-700" 
                                  value={courseForm.department} 
                                  onChange={e => {
                                    const deptId = e.target.value;
                                    const selectedDept = departments.find(d => d._id === deptId);
                                    const deptYears = selectedDept?.activeYears || [];
                                    const newLevel = deptYears.length > 0 ? deptYears[0] : 1;
                                    setCourseForm({ ...courseForm, department: deptId, level: newLevel });
                                  }} 
                                  required
                                >
                                   <option value="">Select Department</option>
                                   {departments.map(d=><option key={d._id} value={d._id}>{d.name}</option>)}
                                </select>
                             </div>
                             <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Year Level</label>
                                <select className="w-full p-3 border border-blue-100 rounded-xl outline-none bg-white focus:ring-2 focus:ring-blue-500 font-semibold text-gray-700" value={courseForm.level} onChange={e=>setCourseForm({...courseForm, level: parseInt(e.target.value)})}>
                                   {(() => {
                                     const selectedDept = departments.find(d => d._id === courseForm.department);
                                     const years = selectedDept?.activeYears && selectedDept.activeYears.length > 0 
                                       ? selectedDept.activeYears 
                                       : [1, 2, 3, 4, 5];
                                     return years.map(y => (
                                       <option key={y} value={y}>Year {y}</option>
                                     ));
                                   })()}
                                </select>
                             </div>

                             <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Credits (ECTS)</label>
                                <input 
                                  type="number" 
                                  min="1" 
                                  max="10" 
                                  className="w-full p-3 border border-blue-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-700 bg-white" 
                                  value={courseForm.credits} 
                                  onChange={e=>setCourseForm({...courseForm, credits: parseInt(e.target.value) || 3})} 
                                  required
                                />
                             </div>
                          </div>
                          <div className="mb-6">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Course Content Description</label>
                            <textarea className="w-full p-3 border border-blue-100 rounded-xl outline-none h-32" value={courseForm.description} onChange={e=>setCourseForm({...courseForm, description: e.target.value})} placeholder="Detailed syllabus or overview..."></textarea>
                          </div>
                          <div className="flex justify-end gap-3">
                             <Button variant="outline" type="button" onClick={()=>setShowForm(false)}>Discard</Button>
                             <Button type="submit">{editingItem ? 'Save Changes' : 'Initialize Course'}</Button>
                          </div>
                       </form>
                    </div>
                  )}

                  <table className="w-full text-left">
                    <thead className="bg-gray-50/50 text-xs font-bold uppercase tracking-wider text-gray-500">
                      <tr>
                        <th className="px-8 py-5">Academic Course</th>
                        <th className="px-8 py-5">Specs</th>
                        <th className="px-8 py-5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {courses.map(course => (
                        <tr key={course._id} className="border-t border-gray-50 hover:bg-gray-50/80 transition-colors">
                          <td className="px-8 py-6">
                             <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-700 flex items-center justify-center font-bold uppercase text-xs">
                                   {course.courseCode}
                                </div>
                                <div>
                                   <span className="font-bold text-gray-900 block">{course.title}</span>
                                   <span className="text-xs text-gray-500 font-medium italic">{course.department?.name}</span>
                                </div>
                             </div>
                          </td>
                          <td className="px-8 py-6">
                             <div className="flex gap-2">
                                <span className="bg-blue-50 text-blue-600 px-2.5 py-1 rounded-lg text-xs font-bold border border-blue-100">Year {course.level || 1}</span>
                                <span className="bg-teal-50 text-teal-600 px-2.5 py-1 rounded-lg text-xs font-bold border border-teal-100">{course.credits || 0} ECTS</span>
                             </div>
                          </td>
                          <td className="px-8 py-6 text-right">
                             <div className="flex justify-end gap-2">
                                <button onClick={() => { setEditingItem(course); setCourseForm({title:course.title, courseCode:course.courseCode, department:course.department?._id || '', description:course.description||'', credits:course.credits||3, semester:course.semester||1, level:course.level||1}); setShowForm(true); }} className="p-2.5 text-blue-500 hover:bg-blue-50 rounded-xl border border-transparent hover:border-blue-100 font-medium"><Edit className="w-4 h-4"/></button>
                                <button onClick={() => handleDeleteCourse(course._id)} className="p-2.5 text-red-500 hover:bg-red-50 rounded-xl border border-transparent hover:border-red-100 font-medium"><Trash2 className="w-4 h-4"/></button>
                             </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'feedback' && (
              <div className="space-y-6 animate-fade-in">
                <div className="flex justify-between items-center">
                   <h2 className="text-2xl font-bold text-gray-800">Student & Staff Feedback</h2>
                   <div className="text-sm font-medium text-gray-500 bg-white px-4 py-2 rounded-xl shadow-sm border border-gray-100 italic">
                      {feedback.length} messages received for your school
                   </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  {feedback.length === 0 ? (
                    <div className="bg-white rounded-3xl p-20 text-center border border-gray-100 shadow-sm">
                       <MessageSquare className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                       <h3 className="text-lg font-bold text-gray-800">No Feedback Yet</h3>
                       <p className="text-gray-500">Feedback specifically for your school will appear here.</p>
                    </div>
                  ) : (
                    feedback.map((msg) => (
                      <div key={msg._id} className={`bg-white rounded-2xl p-6 shadow-sm border-l-4 transition-all duration-300 ${
                        msg.status === 'new' ? 'border-l-blue-500 shadow-md ring-1 ring-blue-50' : 'border-l-gray-200 opacity-80'
                      }`}>
                         <div className="flex flex-col md:flex-row justify-between gap-4">
                            <div className="flex-1">
                               <div className="flex items-center gap-3 mb-2">
                                  <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                                    msg.status === 'new' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {msg.status}
                                  </span>
                                  <span className="text-xs text-gray-400 font-medium">{new Date(msg.createdAt).toLocaleDateString()}</span>
                               </div>
                               <h4 className="text-lg font-bold text-gray-900 mb-1">{msg.subject}</h4>
                               <p className="text-gray-600 mb-4 whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                               <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center font-bold text-gray-400 text-xs">
                                     {msg.fullName[0]}
                                  </div>
                                  <span className="font-semibold">{msg.fullName}</span>
                                  <span>•</span>
                                  <span className="opacity-70">{msg.email}</span>
                               </div>
                            </div>
                            <div className="flex md:flex-col justify-end gap-2">
                               <select 
                                 value={msg.status}
                                 onChange={(e) => handleUpdateFeedbackStatus(msg._id, e.target.value)}
                                 className="text-xs font-bold border-gray-200 rounded-lg p-2 bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
                               >
                                  <option value="new">Mark as New</option>
                                  <option value="read">Mark as Read</option>
                                  <option value="archived">Archive</option>
                               </select>
                               <button 
                                 onClick={() => handleDeleteFeedback(msg._id)}
                                 className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
                                 title="Delete Feedback"
                               >
                                  <Trash className="w-4 h-4" />
                               </button>
                            </div>
                         </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* PERSONAL FILES */}
            {!loading && activeTab === 'personal-files' && (
              <TeacherPortal embeddedVault />
            )}

            {/* CHAT / MESSAGING */}
            {!loading && activeTab === 'chat' && (
              <div className="bg-white rounded-3xl border border-slate-100 shadow-xl overflow-hidden h-[calc(100vh-230px)] md:h-[650px] min-h-[460px] max-h-[700px] flex animate-fade-in">
                {/* Contacts List Column */}
                <div className={`w-full md:w-[350px] border-r border-slate-100 flex flex-col h-full bg-slate-50/50 ${selectedContact ? 'hidden md:flex' : 'flex'}`}>
                  <div className="p-6 border-b border-slate-100 bg-white">
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-800 mb-4">Inbox</h3>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search colleagues..."
                        className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200/60 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                        value={chatSearch}
                        onChange={e => setChatSearch(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
                    {loadingChat ? (
                      <div className="flex flex-col items-center justify-center py-20">
                        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : contacts.filter(c => c.fullName?.toLowerCase().includes(chatSearch.toLowerCase())).length === 0 ? (
                      <div className="text-center py-12 text-slate-400 text-xs font-bold uppercase tracking-wider">No contacts found</div>
                    ) : (
                      contacts.filter(c => c.fullName?.toLowerCase().includes(chatSearch.toLowerCase())).map(contact => {
                        const isSelected = selectedContact?._id === contact._id;
                        return (
                          <button
                            key={contact._id}
                            onClick={() => handleSelectContact(contact)}
                            className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all text-left ${isSelected ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'bg-white hover:bg-slate-100 border border-slate-100/50'}`}
                          >
                            <div className="relative flex-shrink-0">
                              <div className="w-12 h-12 rounded-full overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center">
                                {contact.profileImage && (typeof contact.profileImage === 'string' || contact.profileImage.url) ? (
                                  <img src={publicAPI.getFullImageUrl(typeof contact.profileImage === 'string' ? contact.profileImage : contact.profileImage.url)} className="w-full h-full object-cover" alt="" />
                                ) : (
                                  <span className={`text-lg font-black uppercase ${isSelected ? 'text-blue-600' : 'text-slate-400'}`}>
                                    {contact.fullName?.charAt(0)}
                                  </span>
                                )}
                              </div>
                              <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${contact.role === 'admin' ? 'bg-purple-500' : 'bg-green-500'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-baseline mb-1">
                                <h4 className={`text-xs font-black truncate uppercase tracking-tight ${isSelected ? 'text-white' : 'text-slate-800'}`}>{contact.fullName}</h4>
                                <span className={`text-[8px] font-bold ${isSelected ? 'text-blue-200' : 'text-slate-400'}`}>
                                  {contact.lastMessage ? new Date(contact.lastMessage.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <p className={`text-[10px] truncate ${isSelected ? 'text-blue-100' : 'text-slate-500'} max-w-[160px]`}>
                                  {contact.lastMessage ? contact.lastMessage.content : 'No messages yet'}
                                </p>
                                {contact.unreadCount > 0 && (
                                  <span className="bg-red-500 text-white text-[8px] font-black w-4.5 h-4.5 rounded-full flex items-center justify-center border border-white px-1 animate-pulse">
                                    {contact.unreadCount}
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Messages Chat Stream Column */}
                <div className={`flex-1 flex flex-col h-full bg-white ${!selectedContact ? 'hidden md:flex items-center justify-center' : 'flex'}`}>
                  {selectedContact ? (
                    <>
                      {/* Active Contact Header */}
                      <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/20">
                        <div className="flex items-center gap-4">
                          <button onClick={() => setSelectedContact(null)} className="md:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg"><ArrowLeft className="w-5 h-5" /></button>
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center">
                            {selectedContact.profileImage && (typeof selectedContact.profileImage === 'string' || selectedContact.profileImage.url) ? (
                              <img src={publicAPI.getFullImageUrl(typeof selectedContact.profileImage === 'string' ? selectedContact.profileImage : selectedContact.profileImage.url)} className="w-full h-full object-cover" alt="" />
                            ) : (
                              <span className="text-lg font-black text-slate-400 uppercase">
                                {selectedContact.fullName?.charAt(0)}
                              </span>
                            )}
                          </div>
                          <div>
                            <h4 className="text-xs font-black text-slate-800 uppercase tracking-tight">{selectedContact.fullName}</h4>
                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{selectedContact.role?.replace('_', ' ')}</span>
                          </div>
                        </div>
                      </div>

                      {/* Message History bubble container */}
                      <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar bg-slate-50/20">
                        {loadingMessages ? (
                          <div className="flex items-center justify-center py-20">
                            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        ) : messages.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                            <MessageSquare className="w-10 h-10 mb-2 opacity-30" />
                            <p className="text-[10px] font-black uppercase tracking-widest">Say hello to {selectedContact.fullName}!</p>
                          </div>
                        ) : (
                          messages.map((msg, i) => {
                            const isMe = msg.sender === currentUser?._id || msg.sender?._id === currentUser?._id || msg.sender === currentUser?.id;
                            return (
                              <div key={msg._id || i} className={`flex ${isMe ? 'justify-end' : 'justify-start'} animate-fade-in`}>
                                <div className={`max-w-[70%] p-4 rounded-2xl shadow-sm text-xs font-semibold leading-relaxed ${isMe ? 'bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-tr-none' : 'bg-slate-100 text-slate-800 rounded-tl-none border border-slate-200/50'}`}>
                                  {msg.attachment?.url && (
                                    msg.attachment.fileType === 'image' ? (
                                      <a href={publicAPI.getFullImageUrl(msg.attachment.url)} target="_blank" rel="noreferrer">
                                        <img src={publicAPI.getFullImageUrl(msg.attachment.url)} alt={msg.attachment.originalName} className="rounded-xl max-w-full max-h-48 object-cover mb-2 border border-white/20" />
                                      </a>
                                    ) : (
                                      <a href={publicAPI.getFullImageUrl(msg.attachment.url)} target="_blank" rel="noreferrer" className={`flex items-center gap-2 p-2.5 rounded-xl mb-2 ${isMe ? 'bg-white/15 hover:bg-white/25' : 'bg-white hover:bg-slate-50 border border-slate-200'} transition-colors`}>
                                        <FileText className="w-5 h-5 flex-shrink-0" />
                                        <span className="truncate text-[10px] font-bold">{msg.attachment.originalName || 'Download File'}</span>
                                        <Download className="w-4 h-4 flex-shrink-0 ml-auto" />
                                      </a>
                                    )
                                  )}
                                  {msg.content && <p>{msg.content}</p>}
                                  <div className="flex justify-end items-center gap-1 mt-1 text-[8px] opacity-70">
                                    <span>{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                    {isMe && (
                                      <CheckCheck className={`w-3.5 h-3.5 ${msg.read ? 'text-blue-200' : 'text-slate-300'}`} />
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                        <div ref={messagesEndRef} />
                      </div>

                      {/* File Preview Chip */}
                      {chatAttachment && (
                        <div className="px-4 pt-3 pb-0 bg-white border-t border-slate-100">
                          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 text-xs font-bold text-blue-700">
                            <Paperclip className="w-3.5 h-3.5" />
                            <span className="truncate flex-1">{chatAttachment.name}</span>
                            <button type="button" onClick={() => setChatAttachment(null)} className="p-0.5 hover:bg-blue-100 rounded-full"><X className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      )}
                      {/* Message Send Form Footer */}
                      <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-100 flex gap-3 bg-white">
                        <input type="file" ref={chatFileInputRef} className="hidden" onChange={e => { if (e.target.files[0]) setChatAttachment(e.target.files[0]); e.target.value = ''; }} />
                        <button
                          type="button"
                          onClick={() => chatFileInputRef.current?.click()}
                          className="p-3.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-2xl transition-all flex items-center justify-center"
                          title="Attach a file"
                        >
                          <Paperclip className="w-5 h-5" />
                        </button>
                        <input
                          type="text"
                          placeholder="Type your message here..."
                          className="flex-1 px-4 py-3.5 bg-slate-50 border border-slate-200/60 rounded-2xl text-xs font-bold outline-none focus:ring-2 focus:ring-blue-500 shadow-inner"
                          value={newMessage}
                          onChange={e => setNewMessage(e.target.value)}
                        />
                        <button
                          type="submit"
                          disabled={sendingMsg || (!newMessage.trim() && !chatAttachment)}
                          className="p-3.5 bg-blue-600 text-white rounded-2xl shadow-lg shadow-blue-500/20 hover:bg-blue-700 transition-all flex items-center justify-center disabled:opacity-50"
                        >
                          <Send className="w-5 h-5" />
                        </button>
                      </form>
                    </>
                  ) : (
                    <div className="text-center p-8 space-y-4">
                      <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-[2rem] flex items-center justify-center mx-auto shadow-inner">
                        <MessageSquare className="w-10 h-10" />
                      </div>
                      <h4 className="text-xs font-black uppercase tracking-[0.2em] text-slate-800">Secure Direct Messaging</h4>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest max-w-sm leading-relaxed">Select a teacher colleague or administrator profile from the list to start a confidential dialogue.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'profile' && (
              <div className="max-w-4xl mx-auto animate-slide-up">
                <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                   <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-8 text-white">
                      <div className="flex items-center gap-6">
                         <div className="flex flex-col items-center gap-2">
                            <div className="w-24 h-24 overflow-hidden rounded-3xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/20">
                               {profileImage ? (
                                 <img src={publicAPI.getFullImageUrl(profileImage)} alt="School administrator profile" className="h-full w-full object-cover" />
                               ) : (
                                 <User className="w-12 h-12" />
                               )}
                            </div>
                            <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
                            <button
                              type="button"
                              onClick={() => avatarInputRef.current?.click()}
                              disabled={avatarUploading}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-white/30 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/10 disabled:opacity-60"
                            >
                              <Camera className="h-4 w-4" /> {avatarUploading ? 'Uploading...' : 'Change photo'}
                            </button>
                         </div>
                         <div>
                            <h2 className="text-2xl font-bold">{currentUser.fullName}</h2>
                            <p className="opacity-80">Managing Administrator • {school?.name}</p>
                         </div>
                      </div>
                   </div>
                   
                   <div className="p-10">
                      <form onSubmit={handleProfileUpdate} className="space-y-8">
                         <div className="grid md:grid-cols-2 gap-8">
                            <div className="space-y-6">
                               <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                  <Settings className="w-5 h-5 text-blue-500" />
                                  Account Identity
                               </h3>
                               <div className="space-y-4">
                                  <div>
                                     <label className="block text-xs font-bold text-gray-500 uppercase mb-2 tracking-widest pl-1">Display Name</label>
                                     <input className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-blue-500 bg-white outline-none transition-all shadow-sm focus:shadow-md" value={profileForm.fullName} onChange={e=>setProfileForm({...profileForm, fullName: e.target.value})} required/>
                                  </div>
                                  <div>
                                     <label className="block text-xs font-bold text-gray-500 uppercase mb-2 tracking-widest pl-1">Primary Email</label>
                                     <input className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl text-gray-500 read-only:opacity-70 cursor-not-allowed" value={profileForm.email} readOnly/>
                                     <p className="text-[10px] text-gray-400 mt-1 pl-1 italic">Email cannot be changed by school administrators.</p>
                                  </div>
                               </div>
                            </div>
                            
                            <div className="space-y-6">
                               <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                  <Filter className="w-5 h-5 text-blue-500" />
                                  Contact Info
                               </h3>
                               <div className="space-y-4">
                                  <div>
                                     <label className="block text-xs font-bold text-gray-500 uppercase mb-2 tracking-widest pl-1">Phone Number</label>
                                     <input className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-blue-500 bg-white outline-none shadow-sm focus:shadow-md" value={profileForm.phone} onChange={e=>setProfileForm({...profileForm, phone: e.target.value})} placeholder="+251 9..."/>
                                  </div>
                                  <div>
                                     <label className="block text-xs font-bold text-gray-500 uppercase mb-2 tracking-widest pl-1">Personal Bio</label>
                                     <textarea className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-blue-500 bg-white outline-none h-32 shadow-sm focus:shadow-md" value={profileForm.bio} onChange={e=>setProfileForm({...profileForm, bio: e.target.value})} placeholder="Tell us about your management style..."></textarea>
                                  </div>
                               </div>
                            </div>
                         </div>
                         
                         <div className="pt-6 border-t border-gray-100 flex justify-end">
                            <Button type="submit" loading={loading} className="px-10 py-4 rounded-2xl shadow-lg hover:shadow-blue-200 transition-all font-bold tracking-tight">Save Profile Changes</Button>
                         </div>
                      </form>

                      {/* Security Section */}
                      <div className="mt-12 pt-10 border-t border-gray-100">
                         <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                            <Shield className="w-6 h-6 text-red-500" />
                            Security Settings
                         </h3>
                         <form onSubmit={handlePasswordChange} className="grid md:grid-cols-3 gap-6">
                            <div>
                               <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Current Password</label>
                               <input type="password" px-4 flex-1 className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-red-500 bg-white outline-none" value={passwordForm.currentPassword} onChange={e=>setPasswordForm({...passwordForm, currentPassword: e.target.value})} required/>
                            </div>
                            <div>
                               <label className="block text-xs font-bold text-gray-500 uppercase mb-2">New Password</label>
                               <input type="password" px-4 flex-1 className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-red-500 bg-white outline-none" value={passwordForm.newPassword} onChange={e=>setPasswordForm({...passwordForm, newPassword: e.target.value})} required/>
                            </div>
                            <div className="flex items-end">
                               <Button type="submit" variant="outline" className="w-full border-red-100 text-red-600 hover:bg-red-50 h-[58px] rounded-2xl font-bold">Update Password</Button>
                            </div>
                         </form>
                      </div>
                   </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* MOBILE DRAWER */}
      {isNavMenuOpen && (
        <div className="fixed inset-0 z-[6000] flex lg:hidden">
          <div 
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in" 
            onClick={() => setIsNavMenuOpen(false)} 
          />
          <div className="relative bg-white w-[75%] md:w-[50%] h-full shadow-2xl animate-slide-right flex flex-col">
            <div className="p-8 bg-gradient-to-br from-blue-700 to-indigo-900 text-white">
              <div className="flex justify-between items-center mb-6">
                <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Shield className="w-6 h-6" />
                </div>
                <button onClick={() => setIsNavMenuOpen(false)} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <h3 className="font-black text-lg leading-tight">{currentUser.fullName}</h3>
              <p className="text-[10px] font-bold text-blue-200 uppercase tracking-widest mt-1 opacity-80">{school?.name || 'School Admin'}</p>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-2">
              <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 ml-2">School Control</h4>
              {[
                { id: 'dashboard', label: 'Overview', icon: Shield },
                { id: 'approvals', label: 'Approvals', icon: CheckCircle },
                { id: 'teachers', label: 'Teachers', icon: Users },
                { id: 'departments', label: 'Departments', icon: GraduationCap },
                { id: 'courses', label: 'Courses', icon: BookOpen },
                { id: 'chat', label: 'Colleague Chat', icon: MessageSquare },
                ...(currentUser.permissions?.canUsePersonalFile !== false ? [{ id: 'personal-files', label: 'Personal Files', icon: FileText }] : []),
                { id: 'feedback', label: 'Feedback', icon: MessageSquare },
                { id: 'profile', label: 'My Profile', icon: User }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setShowForm(false); setIsNavMenuOpen(false); }}
                  className={`flex items-center gap-4 w-full p-4 rounded-2xl transition-all ${
                    activeTab === tab.id 
                      ? 'bg-blue-50 text-blue-700 font-black shadow-sm' 
                      : 'text-slate-500 hover:bg-slate-50 font-bold'
                  }`}
                >
                  <div className={`p-2 rounded-xl ${activeTab === tab.id ? 'bg-blue-700 text-white' : 'bg-white shadow-sm'}`}>
                    <tab.icon className="w-5 h-5" />
                  </div>
                  <span className="text-[11px] uppercase tracking-widest">{tab.label}</span>
                  {tab.id === 'feedback' && feedback.filter(f => f.status === 'new').length > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-[9px] font-black px-2 py-1 rounded-full">
                      {feedback.filter(f => f.status === 'new').length}
                    </span>
                  )}
                  {tab.id === 'chat' && totalUnreadChat > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-[9px] font-black px-2 py-1 rounded-full animate-pulse">
                      {totalUnreadChat}
                    </span>
                  )}
                  {tab.id === 'approvals' && pendingTeachers.length > 0 && (
                    <span className="ml-auto bg-orange-500 text-white text-[9px] font-black px-2 py-1 rounded-full">
                      {pendingTeachers.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="p-6 border-t border-slate-100">
              <button
                onClick={() => { handleLogout(); setIsNavMenuOpen(false); }}
                className="flex items-center gap-4 w-full p-4 rounded-2xl bg-red-50 text-red-600 font-black text-[11px] uppercase tracking-widest transition-all hover:bg-red-100"
              >
                <X className="w-5 h-5" />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
      <style dangerouslySetInnerHTML={{ __html: `
        .animate-slide-right { animation: slideRight 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
        .animate-fade-in { animation: fadeIn 0.2s ease-out; }
        @keyframes slideRight { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}} />
    </div>
  );
};

export default SchoolAdminPortal;
